import { getCurrentNickname, setCurrentNickname } from './nicknames';
import type { Game } from './Game';
import { connectOnline, getDefaultServerUrl, watchOnlineRooms, type OnlineSession, type RoomsWatcher } from '../net/online';
import type { ChatMessage, RoomState } from 'shared';

type OnlineDialogOptions = {
  mount: HTMLElement;
  game: Game;
  leaveMenu: () => void;
};

const ensureOnlineDialogStyles = () => {
  if (document.getElementById('online-dialog-styles')) return;

  const style = document.createElement('style');
  style.id = 'online-dialog-styles';
  style.textContent = [
    '.online-dialog-backdrop {',
    '  position: absolute;',
    '  inset: 0;',
    '  z-index: 200;',
    '  display: flex;',
    '  align-items: center;',
    '  justify-content: center;',
    '  padding: 24px;',
    '  background: linear-gradient(rgba(5, 8, 12, .28), rgba(5, 8, 12, .44)), url("/arts/backgrounds/main_bg.jpg") center/cover no-repeat;',
    '  backdrop-filter: blur(10px) saturate(1.18);',
    '  font-family: Arial, sans-serif;',
    '}',
    '.online-dialog {',
    '  width: min(1040px, 100%);',
    '  height: min(720px, calc(100vh - 48px));',
    '  color: #fff;',
    '  display: grid;',
    '  grid-template-rows: auto 1fr;',
    '  overflow: visible;',
    '}',
    '.online-dialog-top {',
    '  display: grid;',
    '  grid-template-columns: minmax(0, 1fr) minmax(180px, 280px);',
    '  align-items: center;',
    '  gap: 12px;',
    '  padding: 0;',
    '}',
    '.online-title-group {',
    '  min-width: 0;',
    '  align-self: stretch;',
    '  display: flex;',
    '  align-items: center;',
    '  gap: 14px;',
    '  padding: 10px 14px;',
    '  border: 1px solid rgba(255, 255, 255, .14);',
    '  border-radius: 8px;',
    '  background: rgba(16, 20, 28, .42);',
    '  box-shadow: 0 18px 44px rgba(0, 0, 0, .26), inset 0 1px 0 rgba(255, 255, 255, .1);',
    '  backdrop-filter: blur(22px) saturate(1.3);',
    '}',
    '.online-back {',
    '  min-height: 40px;',
    '  border: 1px solid rgba(255, 255, 255, .18);',
    '  border-radius: 8px;',
    '  background: rgba(255, 255, 255, .08);',
    '  color: #fff;',
    '  cursor: pointer;',
    '  padding: 0 14px;',
    '  font: inherit;',
    '  font-weight: 900;',
    '  box-shadow: 0 14px 34px rgba(0, 0, 0, .24), inset 0 1px 0 rgba(255, 255, 255, .12);',
    '}',
    '.online-back:hover { border-color: rgba(255, 90, 90, .48); color: #ff7777; background: rgba(255, 70, 70, .12); }',
    '.online-dialog-title {',
    '  color: #ff4c4c;',
    '  font-size: 28px;',
    '  font-weight: 900;',
    '  letter-spacing: 0;',
    '  text-shadow: 0 10px 30px rgba(0, 0, 0, .55), 0 0 18px rgba(255, 76, 76, .28);',
    '  white-space: nowrap;',
    '  min-width: 0;',
    '  overflow: hidden;',
    '  text-overflow: ellipsis;',
    '}',
    '.online-ping-badge {',
    '  margin-left: auto;',
    '  min-width: 94px;',
    '  box-sizing: border-box;',
    '  border: 1px solid rgba(255, 255, 255, .12);',
    '  border-radius: 8px;',
    '  background: rgba(0, 0, 0, .22);',
    '  color: #9aa3ad;',
    '  padding: 8px 10px;',
    '  text-align: right;',
    '  font-size: 13px;',
    '  font-weight: 900;',
    '  white-space: nowrap;',
    '  box-shadow: inset 0 1px 0 rgba(255, 255, 255, .08);',
    '}',
    '.online-name-field {',
    '  width: 100%;',
    '  min-width: 0;',
    '  justify-self: end;',
    '  display: grid;',
    '  gap: 5px;',
    '  box-sizing: border-box;',
    '  padding: 10px 12px 12px;',
    '  border: 1px solid rgba(255, 255, 255, .14);',
    '  border-radius: 8px;',
    '  background: rgba(16, 20, 28, .38);',
    '  color: #ff6666;',
    '  font-size: 12px;',
    '  font-weight: 900;',
    '  box-shadow: 0 18px 44px rgba(0, 0, 0, .26), inset 0 1px 0 rgba(255, 255, 255, .1);',
    '  backdrop-filter: blur(22px) saturate(1.3);',
    '}',
    '.online-name-input {',
    '  width: 100%;',
    '  min-width: 0;',
    '  box-sizing: border-box;',
    '  height: 44px;',
    '  border: 1px solid rgba(255, 255, 255, .16);',
    '  border-radius: 8px;',
    '  background: rgba(255, 255, 255, .08);',
    '  color: #fff;',
    '  padding: 0 14px;',
    '  font: inherit;',
    '  font-weight: 900;',
    '  outline: none;',
    '  box-shadow: inset 0 1px 0 rgba(255, 255, 255, .1);',
    '}',
    '.online-name-input:focus { border-color: rgba(255, 85, 85, .48); box-shadow: 0 0 0 3px rgba(255, 85, 85, .12), inset 0 1px 0 rgba(255, 255, 255, .12); }',
    '.online-body {',
    '  min-height: 0;',
    '  display: grid;',
    '  grid-template-columns: minmax(0, 1fr) 330px;',
    '  gap: 18px;',
    '  padding: 18px 0 0;',
    '}',
    '.online-lobby-side {',
    '  min-height: 0;',
    '  display: grid;',
    '  grid-template-rows: auto 1fr auto;',
    '  gap: 14px;',
    '}',
    '.online-actions {',
    '  display: flex;',
    '  gap: 10px;',
    '  flex-wrap: wrap;',
    '  padding: 12px;',
    '  border: 1px solid rgba(255, 255, 255, .14);',
    '  border-radius: 8px;',
    '  background: rgba(16, 20, 28, .36);',
    '  box-shadow: 0 18px 42px rgba(0, 0, 0, .24), inset 0 1px 0 rgba(255, 255, 255, .1);',
    '  backdrop-filter: blur(22px) saturate(1.3);',
    '}',
    '.online-button {',
    '  min-height: 44px;',
    '  border: 1px solid rgba(255, 255, 255, .16);',
    '  border-radius: 8px;',
    '  background: rgba(255, 255, 255, .08);',
    '  color: #fff;',
    '  cursor: pointer;',
    '  padding: 0 16px;',
    '  font: inherit;',
    '  font-weight: 900;',
    '  box-shadow: inset 0 1px 0 rgba(255, 255, 255, .1);',
    '}',
    '.online-button:hover { border-color: rgba(255, 90, 90, .45); color: #ff7777; background: rgba(255, 70, 70, .12); }',
    '.online-button.primary { border-color: rgba(255, 90, 90, .42); color: #ff7777; background: rgba(255, 70, 70, .12); }',
    '.online-button.start { margin-left: auto; border-color: rgba(120, 255, 150, .34); color: #aaffb8; background: rgba(60, 200, 95, .12); }',
    '.online-button.danger { border-color: rgba(255, 90, 90, .45); color: #ff9a9a; background: rgba(255, 55, 55, .16); }',
    '.online-button:disabled { opacity: .42; cursor: not-allowed; }',
    '.online-lobbies {',
    '  min-height: 0;',
    '  overflow: auto;',
    '  display: grid;',
    '  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));',
    '  grid-auto-rows: minmax(178px, max-content);',
    '  gap: 12px;',
    '  padding-right: 4px;',
    '}',
    '.online-lobby-card {',
    '  border: 1px solid rgba(255, 255, 255, .14);',
    '  border-radius: 8px;',
    '  background: rgba(16, 20, 28, .38);',
    '  padding: 14px;',
    '  display: grid;',
    '  grid-template-rows: auto 1fr;',
    '  gap: 12px;',
    '  cursor: pointer;',
    '  box-shadow: 0 16px 36px rgba(0, 0, 0, .24), inset 0 1px 0 rgba(255, 255, 255, .09);',
    '  backdrop-filter: blur(22px) saturate(1.3);',
    '}',
    '.online-lobby-card:hover, .online-lobby-card.active { border-color: rgba(255, 90, 90, .44); box-shadow: 0 18px 46px rgba(0, 0, 0, .3), 0 0 28px rgba(255, 70, 70, .12), inset 0 1px 0 rgba(255, 255, 255, .12); }',
    '.online-lobby-card.active { cursor: default; }',
    '.online-lobby-head { display: flex; justify-content: space-between; align-items: center; gap: 10px; }',
    '.online-lobby-code { color: #ff4444; font-family: monospace; font-size: 24px; font-weight: 900; }',
    '.online-lobby-count { color: #9fff9f; font-size: 13px; font-weight: 900; }',
    '.online-lobby-players { display: grid; gap: 7px; align-content: start; }',
    '.online-player-mini { display: grid; grid-template-columns: 28px 1fr; align-items: center; gap: 8px; min-width: 0; color: #ddd; font-weight: 900; }',
    '.online-player-mini span:first-child { width: 28px; height: 28px; border-radius: 50%; display: grid; place-items: center; background: #cc2222; color: #fff; font-size: 11px; }',
    '.online-player-mini span:last-child { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }',
    '.online-player-mini.own {',
    '  margin-left: -6px;',
    '  padding: 4px 6px;',
    '  border: 1px solid rgba(255, 90, 90, .48);',
    '  border-radius: 8px;',
    '  background: rgba(255, 70, 70, .12);',
    '  color: #fff;',
    '}',
    '.online-player-mini.own span:first-child { box-shadow: 0 0 16px rgba(255, 68, 68, .5); }',
    '.online-player-mini strong { color: #9fff9f; font-size: 11px; margin-left: 6px; }',
    '.online-empty { opacity: .52; border-style: dashed; cursor: default; place-items: center; text-align: center; color: #888; font-weight: 900; }',
    '.online-lobby-card.online-empty {',
    '  opacity: 1;',
    '  border-style: solid;',
    '  border-color: rgba(255, 255, 255, .18);',
    '  background: rgba(18, 24, 34, .72);',
    '  color: rgba(255, 255, 255, .82);',
    '  text-shadow: 0 2px 12px rgba(0, 0, 0, .45);',
    '}',
    '.online-status { min-height: 24px; color: #888; font-size: 13px; font-weight: 900; }',
    '.online-status.error { color: #ff7777; }',
    '.online-alert {',
    '  position: fixed;',
    '  top: 18px;',
    '  left: 50%;',
    '  z-index: 500;',
    '  width: min(420px, calc(100vw - 32px));',
    '  box-sizing: border-box;',
    '  border: 1px solid rgba(255, 100, 100, .38);',
    '  border-radius: 8px;',
    '  background: rgba(42, 18, 25, .78);',
    '  color: #fff;',
    '  padding: 12px 14px;',
    '  font-weight: 900;',
    '  text-align: center;',
    '  box-shadow: 0 22px 48px rgba(0, 0, 0, .36), inset 0 1px 0 rgba(255, 255, 255, .14);',
    '  backdrop-filter: blur(24px) saturate(1.35);',
    '  pointer-events: none;',
    '  opacity: 0;',
    '  transform: translate(-50%, -18px) scale(.98);',
    '}',
    '.online-alert.show { animation: onlineToast 2s ease forwards; }',
    '.online-alert[hidden] { display: none; }',
    '@keyframes onlineToast {',
    '  0% { opacity: 0; transform: translate(-50%, -18px) scale(.98); }',
    '  12% { opacity: 1; transform: translate(-50%, 0) scale(1); }',
    '  82% { opacity: 1; transform: translate(-50%, 0) scale(1); }',
    '  100% { opacity: 0; transform: translate(-50%, -12px) scale(.98); }',
    '}',
    '.online-confirm-backdrop {',
    '  position: fixed;',
    '  inset: 0;',
    '  z-index: 460;',
    '  display: grid;',
    '  place-items: center;',
    '  padding: 24px;',
    '  background: rgba(0, 0, 0, .34);',
    '  backdrop-filter: blur(8px);',
    '}',
    '.online-confirm-backdrop[hidden] { display: none; }',
    '.online-confirm {',
    '  width: min(360px, 100%);',
    '  border: 1px solid rgba(255, 255, 255, .16);',
    '  border-radius: 8px;',
    '  background: rgba(18, 22, 30, .78);',
    '  color: #fff;',
    '  padding: 18px;',
    '  box-shadow: 0 24px 70px rgba(0, 0, 0, .42), inset 0 1px 0 rgba(255, 255, 255, .12);',
    '  backdrop-filter: blur(28px) saturate(1.35);',
    '}',
    '.online-confirm-title { color: #ff6666; font-size: 20px; font-weight: 900; margin-bottom: 8px; }',
    '.online-confirm-text { color: rgba(255, 255, 255, .82); font-size: 14px; font-weight: 800; line-height: 1.35; margin-bottom: 16px; }',
    '.online-confirm-actions { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }',
    '.online-confirm-button {',
    '  min-height: 42px;',
    '  border: 1px solid rgba(255, 255, 255, .16);',
    '  border-radius: 8px;',
    '  background: rgba(255, 255, 255, .08);',
    '  color: #fff;',
    '  cursor: pointer;',
    '  font: inherit;',
    '  font-weight: 900;',
    '}',
    '.online-confirm-button:hover { border-color: rgba(255, 255, 255, .28); background: rgba(255, 255, 255, .12); }',
    '.online-confirm-button.danger { border-color: rgba(255, 90, 90, .42); color: #ff8d8d; background: rgba(255, 70, 70, .14); }',
    '.online-chat {',
    '  min-height: 0;',
    '  border: 1px solid rgba(255, 255, 255, .14);',
    '  border-radius: 8px;',
    '  background: rgba(16, 20, 28, .38);',
    '  display: grid;',
    '  grid-template-rows: auto 1fr auto;',
    '  overflow: hidden;',
    '  box-shadow: 0 20px 54px rgba(0, 0, 0, .28), inset 0 1px 0 rgba(255, 255, 255, .1);',
    '  backdrop-filter: blur(24px) saturate(1.35);',
    '}',
    '.online-chat-head { padding: 13px 14px; border-bottom: 1px solid rgba(255, 255, 255, .12); color: #ff5c5c; font-weight: 900; letter-spacing: 0; }',
    '.online-chat-messages { min-height: 0; overflow: auto; padding: 12px; display: flex; flex-direction: column; gap: 8px; }',
    '.online-chat-bubble { max-width: 86%; border-radius: 14px 14px 14px 4px; background: #1e2f3f; color: #f4f8ff; padding: 9px 11px; line-height: 1.25; word-break: break-word; }',
    '.online-chat-bubble.own { align-self: flex-end; border-radius: 14px 14px 4px 14px; background: #3f6f9f; }',
    '.online-chat-name { color: rgba(255,255,255,.64); font-size: 11px; font-weight: 900; margin-bottom: 3px; }',
    '.online-chat-form { display: grid; grid-template-columns: 1fr auto; gap: 8px; padding: 12px; border-top: 1px solid rgba(255, 255, 255, .12); }',
    '.online-chat-form input { min-width: 0; height: 40px; border: 1px solid rgba(255, 255, 255, .14); border-radius: 8px; background: rgba(255, 255, 255, .08); color: #fff; padding: 0 12px; font: inherit; outline: none; }',
    '.online-chat-form input:focus { border-color: rgba(255, 90, 90, .44); }',
    '.online-chat-form button { width: 46px; border: 1px solid rgba(120, 255, 150, .34); border-radius: 8px; background: rgba(60, 200, 95, .12); color: #aaffb8; cursor: pointer; font-weight: 900; }',
    '.online-chat-form :disabled { opacity: .45; cursor: not-allowed; }',
    '@media (max-width: 820px) {',
    '  .online-dialog { height: calc(100vh - 24px); }',
    '  .online-dialog-top { grid-template-columns: 1fr; }',
    '  .online-ping-badge { min-width: 82px; font-size: 12px; }',
    '  .online-name-field { grid-column: 1 / -1; justify-self: stretch; }',
    '  .online-body { grid-template-columns: 1fr; grid-template-rows: minmax(0, 1fr) 260px; }',
    '}',
  ].join('\n');
  document.head.appendChild(style);
};

export const showOnlineDialog = ({ mount, game, leaveMenu }: OnlineDialogOptions) => {
  ensureOnlineDialogStyles();

  let session: OnlineSession | null = null;
  let started = false;
  let connecting = false;
  let roomsWatcher: RoomsWatcher | null = null;
  let currentRoom: RoomState | null = null;
  let alertTimer: number | null = null;
  let confirmResolver: ((confirmed: boolean) => void) | null = null;
  let knownRooms: RoomState[] = [];
  const chatLog: ChatMessage[] = [];

  const overlay = document.createElement('div');
  overlay.className = 'online-dialog-backdrop';
  overlay.innerHTML = [
    '<div class="online-alert" id="online-alert" hidden></div>',
    '<div class="online-confirm-backdrop" id="online-confirm-backdrop" hidden>',
    '  <div class="online-confirm" role="dialog" aria-modal="true" aria-labelledby="online-confirm-title">',
    '    <div class="online-confirm-title" id="online-confirm-title">Удалить лобби?</div>',
    '    <div class="online-confirm-text">Комната закроется для всех игроков.</div>',
    '    <div class="online-confirm-actions">',
    '      <button class="online-confirm-button" id="online-confirm-cancel" type="button">Отмена</button>',
    '      <button class="online-confirm-button danger" id="online-confirm-ok" type="button">Удалить</button>',
    '    </div>',
    '  </div>',
    '</div>',
    '<div class="online-dialog">',
    '  <div class="online-dialog-top">',
    '    <div class="online-title-group">',
    '      <button class="online-back" id="online-back" type="button">Назад</button>',
    '      <div class="online-dialog-title">ОНЛАЙН ТЕСТ</div>',
    '      <div class="online-ping-badge" id="online-ping">пинг -- мс</div>',
    '    </div>',
    '    <label class="online-name-field"><span>Ник</span><input class="online-name-input" id="online-name" maxlength="24" autocomplete="off" aria-label="Ник" /></label>',
    '  </div>',
    '  <div class="online-body">',
    '    <div class="online-lobby-side">',
    '      <div class="online-actions">',
    '        <button class="online-button primary" id="online-create" type="button">Создать лобби</button>',
    '        <button class="online-button" id="online-refresh" type="button">Обновить</button>',
    '        <button class="online-button start" id="online-start" type="button" disabled>Заскочить</button>',
    '      </div>',
    '      <div class="online-lobbies" id="online-lobbies"></div>',
    '      <div class="online-status" id="online-status"></div>',
    '    </div>',
    '    <div class="online-chat">',
    '      <div class="online-chat-head" id="online-chat-head">ЧАТ</div>',
    '      <div class="online-chat-messages" id="online-chat-messages"></div>',
    '      <form class="online-chat-form" id="online-chat-form">',
    '        <input id="online-chat-input" maxlength="280" placeholder="сообщение" disabled />',
    '        <button id="online-chat-send" type="submit" disabled>↵</button>',
    '      </form>',
    '    </div>',
    '  </div>',
    '</div>',
  ].join('');
  mount.appendChild(overlay);

  const nameInput = overlay.querySelector('#online-name') as HTMLInputElement;
  const lobbies = overlay.querySelector('#online-lobbies') as HTMLDivElement;
  const status = overlay.querySelector('#online-status') as HTMLDivElement;
  const alertBox = overlay.querySelector('#online-alert') as HTMLDivElement;
  const confirmBackdrop = overlay.querySelector('#online-confirm-backdrop') as HTMLDivElement;
  const confirmCancel = overlay.querySelector('#online-confirm-cancel') as HTMLButtonElement;
  const confirmOk = overlay.querySelector('#online-confirm-ok') as HTMLButtonElement;
  const chatHead = overlay.querySelector('#online-chat-head') as HTMLDivElement;
  const chatMessages = overlay.querySelector('#online-chat-messages') as HTMLDivElement;
  const chatForm = overlay.querySelector('#online-chat-form') as HTMLFormElement;
  const chatInput = overlay.querySelector('#online-chat-input') as HTMLInputElement;
  const chatSend = overlay.querySelector('#online-chat-send') as HTMLButtonElement;
  const startButton = overlay.querySelector('#online-start') as HTMLButtonElement;
  const createButton = overlay.querySelector('#online-create') as HTMLButtonElement;
  const refreshButton = overlay.querySelector('#online-refresh') as HTMLButtonElement;
  const backButton = overlay.querySelector('#online-back') as HTMLButtonElement;
  const pingBadge = overlay.querySelector('#online-ping') as HTMLDivElement;

  nameInput.value = getCurrentNickname();
  nameInput.addEventListener('input', () => {
    if (nameInput.value.trim()) setCurrentNickname(nameInput.value);
  });

  const setStatus = (text: string, error = false) => {
    status.textContent = text;
    status.classList.toggle('error', error);
  };

  const setPing = (pingMs: number | null) => {
    pingBadge.textContent = pingMs === null ? 'пинг -- мс' : `пинг ${pingMs} мс`;
    pingBadge.style.color = pingMs === null
      ? '#9aa3ad'
      : pingMs <= 80
        ? '#9dff9d'
        : pingMs <= 140
          ? '#ffdf76'
          : '#ff6b6b';
  };

  const showAlert = (text: string) => {
    if (alertTimer !== null) window.clearTimeout(alertTimer);
    alertBox.textContent = text;
    alertBox.hidden = false;
    alertBox.classList.remove('show');
    void alertBox.offsetWidth;
    alertBox.classList.add('show');
    alertTimer = window.setTimeout(() => {
      hideAlert();
    }, 2000);
  };

  const hideAlert = () => {
    if (alertTimer !== null) {
      window.clearTimeout(alertTimer);
      alertTimer = null;
    }
    alertBox.classList.remove('show');
    alertBox.hidden = true;
    alertBox.textContent = '';
  };

  const closeConfirm = (confirmed: boolean) => {
    confirmBackdrop.hidden = true;
    const resolver = confirmResolver;
    confirmResolver = null;
    resolver?.(confirmed);
  };

  const confirmDeleteLobby = () => new Promise<boolean>(resolve => {
    confirmResolver?.(false);
    confirmResolver = resolve;
    confirmBackdrop.hidden = false;
    confirmOk.focus();
  });

  confirmCancel.onclick = () => closeConfirm(false);
  confirmOk.onclick = () => closeConfirm(true);
  confirmBackdrop.addEventListener('click', event => {
    if (event.target === confirmBackdrop) closeConfirm(false);
  });

  const escapeHtml = (value: string) => value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const renderChat = () => {
    chatMessages.innerHTML = chatLog.map(message => {
      const own = message.playerId === session?.playerId;
      return '<div class="online-chat-bubble' + (own ? ' own' : '') + '">' +
        '<div class="online-chat-name">' + escapeHtml(message.name) + '</div>' +
        escapeHtml(message.text) +
      '</div>';
    }).join('');
    chatMessages.scrollTop = chatMessages.scrollHeight;
  };

  const isRoomAdmin = () => Boolean(session && currentRoom?.ownerId === session.playerId);

  const syncLobbyControls = () => {
    const inRoom = Boolean(session && currentRoom);
    const admin = isRoomAdmin();
    createButton.textContent = admin ? 'Удалить лобби' : inRoom ? 'Выйти из лобби' : 'Создать лобби';
    createButton.classList.toggle('danger', admin);
    createButton.classList.toggle('primary', !admin);
    createButton.disabled = connecting;
    refreshButton.disabled = connecting;
    startButton.disabled = connecting || !inRoom;
    chatInput.disabled = !inRoom;
    chatSend.disabled = !inRoom;
  };

  const resetLobby = (message: string) => {
    session = null;
    currentRoom = null;
    chatLog.length = 0;
    chatHead.textContent = 'ЧАТ';
    renderChat();
    hideAlert();
    setStatus(message);
    syncLobbyControls();
    requestRooms();
  };

  const closeOnlineDialog = (keepSession = false) => {
    roomsWatcher?.close();
    roomsWatcher = null;
    if (!keepSession) session?.close();
    hideAlert();
    closeConfirm(false);
    overlay.remove();
  };

  const getRoomNumber = (roomId: RoomState['roomId']) => {
    const match = /^room(\d+)$/.exec(roomId);
    return match ? Number(match[1]) : Number.POSITIVE_INFINITY;
  };

  const compareRooms = (left: RoomState, right: RoomState) => {
    const leftNumber = getRoomNumber(left.roomId);
    const rightNumber = getRoomNumber(right.roomId);
    if (leftNumber !== rightNumber) return leftNumber - rightNumber;
    return left.roomId.localeCompare(right.roomId);
  };

  const renderRooms = (rooms: RoomState[]) => {
    const activeRoomId = currentRoom?.roomId ?? session?.roomId;
    const roomMap = new Map(rooms.map(room => [room.roomId, room]));
    if (currentRoom) roomMap.set(currentRoom.roomId, currentRoom);
    const sortedRooms = [...roomMap.values()].sort(compareRooms);

    if (sortedRooms.length === 0) {
      lobbies.innerHTML = '<div class="online-lobby-card online-empty">Лобби пока нет</div>';
      syncLobbyControls();
      return;
    }

    lobbies.innerHTML = sortedRooms.map(room => {
      const playersHtml = room.players.length > 0
        ? room.players.map(player => {
          const own = player.id === session?.playerId;
          return '<div class="online-player-mini' + (own ? ' own' : '') + '"><span>' + escapeHtml(player.name.slice(0, 2).toUpperCase()) + '</span><span>' + escapeHtml(player.name) + (own ? '<strong>вы</strong>' : '') + '</span></div>';
        }).join('')
        : '<div class="online-player-mini"><span>+</span><span>свободно</span></div>';
      return '<button class="online-lobby-card' + (room.roomId === activeRoomId ? ' active' : '') + '" data-room-id="' + escapeHtml(room.roomId) + '" type="button">' +
        '<div class="online-lobby-head"><div class="online-lobby-code">' + escapeHtml(room.roomId) + '</div><div class="online-lobby-count">' + room.players.length + '/' + room.maxPlayers + '</div></div>' +
        '<div class="online-lobby-players">' + playersHtml + '</div>' +
      '</button>';
    }).join('');

    lobbies.querySelectorAll<HTMLButtonElement>('[data-room-id]').forEach(card => {
      card.disabled = card.dataset.roomId === activeRoomId;
      card.addEventListener('click', () => void connect('join', card.dataset.roomId));
    });
    syncLobbyControls();
  };

  const requestRooms = () => {
    roomsWatcher?.requestRooms();
  };

  const setRoom = (room: RoomState) => {
    currentRoom = room;
    knownRooms = [...knownRooms.filter(knownRoom => knownRoom.roomId !== room.roomId), room];
    chatHead.textContent = 'ЧАТ ' + room.roomId;
    setStatus(room.roomId);
    renderRooms(knownRooms);
    syncLobbyControls();
    requestRooms();
  };

  const connect = async (mode: 'create' | 'join', roomId?: string) => {
    const targetRoomId = roomId?.trim();
    if (mode === 'create' && session) {
      showAlert('Сначала удали свою комнату.');
      return;
    }
    if (mode === 'join' && !targetRoomId) return;
    const currentSession = session;
    if (mode === 'join' && currentSession && currentSession.roomId === targetRoomId) {
      setStatus(currentSession.roomId);
      return;
    }
    if (mode === 'join' && currentSession && isRoomAdmin()) {
      showAlert('Сначала удали свою комнату.');
      return;
    }
    if (connecting) return;

    hideAlert();
    connecting = true;
    const name = setCurrentNickname(nameInput.value);
    nameInput.value = name;
    syncLobbyControls();
    setStatus(mode === 'create' ? 'Создаем лобби...' : 'Входим в ' + targetRoomId + '...');

    try {
      const previousSession = session;
      const nextSession = await connectOnline({
        roomId: mode === 'join' ? targetRoomId : undefined,
        name,
        serverUrl: getDefaultServerUrl(),
      });
      previousSession?.close();
      session = nextSession;
      session.onPing(setPing);
      chatLog.length = 0;
      chatLog.push(...session.chatHistory.slice(-10));
      renderChat();
      setRoom(session.room);
      session.onRoomState(setRoom);
      session.onChat(message => {
        chatLog.push(message);
        if (chatLog.length > 40) chatLog.shift();
        renderChat();
      });
      session.onRoomDeleted(roomId => {
        knownRooms = knownRooms.filter(room => room.roomId !== roomId);
        resetLobby(roomId + ' удалено.');
      });
    } catch (error) {
      showAlert(error instanceof Error ? error.message : 'Не удалось подключиться');
      setStatus(error instanceof Error ? error.message : 'Не удалось подключиться', true);
    } finally {
      connecting = false;
      syncLobbyControls();
    }
  };

  createButton.onclick = async () => {
    if (session && isRoomAdmin()) {
      const confirmed = await confirmDeleteLobby();
      if (!confirmed) return;
      hideAlert();
      setStatus('Удаляем лобби...');
      session.deleteRoom();
      return;
    }

    if (session) {
      const leftRoomId = session.roomId;
      session.close();
      resetLobby('Вы вышли из ' + leftRoomId + '.');
      return;
    }

    void connect('create');
  };
  refreshButton.onclick = requestRooms;
  startButton.onclick = () => {
    if (!session) return;
    started = true;
    leaveMenu();
    closeOnlineDialog(true);
    game.setOnlineSession(session);
    game.start();
    game.gotoLevel(1);
  };
  backButton.onclick = () => {
    closeOnlineDialog(started);
  };
  chatForm.onsubmit = event => {
    event.preventDefault();
    if (!session) return;
    session.sendChat(chatInput.value);
    chatInput.value = '';
  };

  roomsWatcher = watchOnlineRooms({
    serverUrl: getDefaultServerUrl(),
    onRooms: rooms => {
      knownRooms = rooms;
      renderRooms(knownRooms);
      if (!currentRoom) setStatus(rooms.length ? '' : 'Создай первое лобби.');
    },
    onError: error => {
      setStatus(error.message, true);
    },
    onPing: setPing,
  });
  syncLobbyControls();
};
